import {
    Component, Input, ChangeDetectionStrategy, ChangeDetectorRef,
    HostListener, OnDestroy
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared/shared.module';
import { TEMPLATE_EDITOR_TOUR_SEEN_PREFIX } from 'src/app/shared/constants/localStorage.constants';

export interface EditorTourStep {
    targetSelector: string;
    titleKey: string;
    messageKey: string;
}

interface TooltipPosition {
    top: number;
    left: number;
    placement: 'above' | 'below' | 'right' | 'left';
}

@Component({
    standalone: true,
    selector: 'app-editor-tour',
    imports: [SharedModule],
    templateUrl: './editor-tour.component.html',
    styleUrls: ['./editor-tour.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditorTourComponent implements OnDestroy {
    @Input() steps: EditorTourStep[] = [];
    @Input() tourId = 'default';

    isOpen = false;
    currentIndex = 0;
    highlightRect: DOMRect | null = null;
    tooltipPosition: TooltipPosition = { top: 0, left: 0, placement: 'below' };
    activeSteps: EditorTourStep[] = [];

    private rafId?: number;

    constructor(private cdr: ChangeDetectorRef, private translate: TranslateService) {}

    get stepCounterText(): string {
        return this.translate.instant('Step {{current}} of {{total}}', {
            current: this.currentIndex + 1,
            total: this.totalSteps
        });
    }

    get currentStep(): EditorTourStep | undefined {
        return this.activeSteps[this.currentIndex];
    }

    get isLastStep(): boolean {
        return this.currentIndex >= this.activeSteps.length - 1;
    }

    get totalSteps(): number {
        return this.activeSteps.length;
    }

    private get storageKey(): string {
        return `${TEMPLATE_EDITOR_TOUR_SEEN_PREFIX}${this.tourId}`;
    }

    async startIfFirstVisit(): Promise<void> {
        if (localStorage.getItem(this.storageKey) === 'true') return;
        if (!this.steps.length) return;

        const reachable: EditorTourStep[] = [];
        for (const step of this.steps) {
            const target = await this.waitForTarget(step.targetSelector, 1500);
            if (target) reachable.push(step);
        }
        if (!reachable.length) return;

        this.activeSteps = reachable;
        this.currentIndex = 0;
        this.isOpen = true;
        this.cdr.markForCheck();
        this.scheduleReposition();
    }

    next(): void {
        if (this.isLastStep) {
            this.complete();
            return;
        }
        this.currentIndex++;
        this.cdr.markForCheck();
        this.scheduleReposition();
    }

    skip(): void {
        this.complete();
    }

    private complete(): void {
        localStorage.setItem(this.storageKey, 'true');
        this.isOpen = false;
        this.highlightRect = null;
        this.cdr.markForCheck();
    }

    private scheduleReposition(): void {
        if (this.rafId !== undefined) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
            this.reposition();
            this.rafId = requestAnimationFrame(() => this.reposition());
        });
    }

    private reposition(): void {
        const step = this.currentStep;
        if (!step) return;
        const target = document.querySelector(step.targetSelector) as HTMLElement | null;
        if (!target) return;
        target.scrollIntoView({ block: 'center', behavior: 'auto' });
        this.highlightRect = target.getBoundingClientRect();
        this.tooltipPosition = this.computeTooltipPosition(this.highlightRect);
        this.cdr.markForCheck();
    }

    private waitForTarget(selector: string, timeoutMs: number): Promise<HTMLElement | null> {
        return new Promise(resolve => {
            const start = Date.now();
            const tick = () => {
                const el = document.querySelector(selector) as HTMLElement | null;
                if (el) { resolve(el); return; }
                if (Date.now() - start >= timeoutMs) { resolve(null); return; }
                setTimeout(tick, 60);
            };
            tick();
        });
    }

    private computeTooltipPosition(rect: DOMRect): TooltipPosition {
        const tooltipWidth = 320;
        const tooltipHeight = 170;
        const margin = 16;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const centerLeft = Math.min(
            Math.max(margin, rect.left + rect.width / 2 - tooltipWidth / 2),
            vw - tooltipWidth - margin
        );

        if (rect.bottom + margin + tooltipHeight < vh) {
            return { top: rect.bottom + margin, left: centerLeft, placement: 'below' };
        }
        if (rect.top - margin - tooltipHeight > 0) {
            return { top: rect.top - margin - tooltipHeight, left: centerLeft, placement: 'above' };
        }
        if (rect.right + margin + tooltipWidth < vw) {
            return { top: Math.max(margin, rect.top), left: rect.right + margin, placement: 'right' };
        }
        return {
            top: Math.max(margin, rect.top),
            left: Math.max(margin, rect.left - tooltipWidth - margin),
            placement: 'left'
        };
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    @HostListener('window:scroll')
    onScroll(): void {
        if (this.isOpen) this.scheduleReposition();
    }

    ngOnDestroy(): void {
        if (this.rafId !== undefined) cancelAnimationFrame(this.rafId);
    }
}
